import React, { useEffect, useRef, useCallback } from "react";

// Configurazione base per l'effetto Nebulosa
const nebulaConfig = {
  nebulaCount: 15,
  minNebulaSize: 150,
  maxNebulaSize: 400,
  speed: 0.04,
  colors: [
    "rgba(80, 150, 255,", // Blu medio-chiaro
    "rgba(180, 80, 255,", // Viola brillante
    "rgba(0, 200, 255,", // Ciano brillante
    "rgba(255, 100, 150,", // Rosa caldo
    "rgba(100, 255, 100,", // Verde brillante
    "rgba(255, 220, 100,", // Giallo tenue
    "rgba(200, 200, 200,", // Bianco grigiastro
  ],
  maxOpacity: 0.4,
};

// Classe NebulaBlob
class NebulaBlob {
  constructor(canvasWidth, canvasHeight) {
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.reset();
  }

  reset() {
    this.size = Math.random() * (nebulaConfig.maxNebulaSize - nebulaConfig.minNebulaSize) + nebulaConfig.minNebulaSize;

    const halfSize = this.size / 2;
    this.x = halfSize + Math.random() * (this.canvasWidth - 2 * halfSize);
    this.y = halfSize + Math.random() * (this.canvasHeight - 2 * halfSize);

    this.vx = (Math.random() - 0.5) * nebulaConfig.speed;
    this.vy = (Math.random() - 0.5) * nebulaConfig.speed;
    this.color = nebulaConfig.colors[Math.floor(Math.random() * nebulaConfig.colors.length)];
    this.opacity = Math.random() * nebulaConfig.maxOpacity;
  }

  update() {
    this.x += this.vx;
    this.y += this.vy;

    if (this.x < -this.size) this.x = this.canvasWidth + this.size;
    if (this.x > this.canvasWidth + this.size) this.x = -this.size;
    if (this.y < -this.size) this.y = this.canvasHeight + this.size;
    if (this.y > this.canvasHeight + this.size) this.y = -this.size;
  }

  draw(ctx) {
    const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size);
    gradient.addColorStop(0, `${this.color}${this.opacity})`);
    gradient.addColorStop(1, `${this.color}0)`);

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.fill();
  }
}

const GalaxyBackgroundCanvas = ({ className }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const nebulaBlobsRef = useRef([]);
  const resizeTimeoutRef = useRef(null);
  const lastFrameTimeRef = useRef(0);
  const overlayGradientRef = useRef(null);

  const fps = 20;
  const fpsInterval = 1000 / fps;

  // Config responsivo
  const getResponsiveConfig = () => {
    const width = window.innerWidth;
    if (width < 768) {
      return { nebulaCount: 8, minNebulaSize: 100, maxNebulaSize: 250 };
    }
    return { nebulaCount: 15, minNebulaSize: 150, maxNebulaSize: 400 };
  };

  const animate = useCallback(
    (timestamp) => {
      if (!lastFrameTimeRef.current) lastFrameTimeRef.current = timestamp;
      const elapsed = timestamp - lastFrameTimeRef.current;

      if (elapsed > fpsInterval) {
        lastFrameTimeRef.current = timestamp - (elapsed % fpsInterval);

        const canvas = canvasRef.current;
        if (!canvas) {
          animationRef.current = requestAnimationFrame(animate);
          return;
        }

        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        nebulaBlobsRef.current.forEach((blob) => {
          blob.update();
          blob.draw(ctx);
        });

        if (!overlayGradientRef.current) {
          overlayGradientRef.current = ctx.createLinearGradient(0, 0, 0, canvas.height);
          overlayGradientRef.current.addColorStop(0, "rgba(0,0,0,0.5)");
          overlayGradientRef.current.addColorStop(0.3, "rgba(0,0,0,0.3)");
          overlayGradientRef.current.addColorStop(1, "rgba(0,0,0,0)");
        }
        ctx.fillStyle = overlayGradientRef.current;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      animationRef.current = requestAnimationFrame(animate);
    },
    [fpsInterval]
  );

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = { width: window.innerWidth, height: window.innerHeight }; // sempre dimensioni complete
    const dpr = Math.min(window.devicePixelRatio || 1, window.innerWidth < 768 ? 1.5 : 2);

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    // Aggiorna configurazione responsiva
    const { nebulaCount, minNebulaSize, maxNebulaSize } = getResponsiveConfig();
    Object.assign(nebulaConfig, { nebulaCount, minNebulaSize, maxNebulaSize });

    if (nebulaBlobsRef.current.length !== nebulaConfig.nebulaCount || nebulaBlobsRef.current.length === 0) {
      nebulaBlobsRef.current = Array.from(
        { length: nebulaConfig.nebulaCount },
        () => new NebulaBlob(rect.width, rect.height)
      );
    } else {
      nebulaBlobsRef.current.forEach((blob) => {
        blob.canvasWidth = rect.width;
        blob.canvasHeight = rect.height;
        blob.reset();
      });
    }

    overlayGradientRef.current = null;
  }, []);

  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
    resizeTimeoutRef.current = setTimeout(() => {
      initCanvas();
    }, 250);
  }, [initCanvas]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      initCanvas();
      animationRef.current = requestAnimationFrame(animate);
    }, 0); // assicura dimensioni corrette dopo render

    window.addEventListener("resize", handleResize);

    // Ottimizzazione: pausa animazione se scheda non visibile
    const handleVisibilityChange = () => {
      if (document.hidden && animationRef.current) cancelAnimationFrame(animationRef.current);
      else if (!document.hidden) animationRef.current = requestAnimationFrame(animate);
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
    };
  }, [animate, handleResize, initCanvas]);

  return <canvas ref={canvasRef} className={className} />;
};

export default GalaxyBackgroundCanvas;
